import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import Modal from '../../components/modal'
import useI18n from '../../i18n/useI18n'
import { Actions as ApiCallActions } from '../../redux/apiCall/reducers'
import { ApplicationStatusOptions, ApplicationStatusEnum } from '../../utils/enums'
import './ApplicationStatusHistoryModal.scss'

const statusColorMap = {
  [ApplicationStatusEnum.DRAFT]: '#6c757d',
  [ApplicationStatusEnum.SUBMITTED]: '#17a2b8',
  [ApplicationStatusEnum.UNDER_REVIEW]: '#ffc107',
  [ApplicationStatusEnum.ACCEPTED]: '#28a745',
  [ApplicationStatusEnum.REJECTED]: '#dc3545',
  [ApplicationStatusEnum.WAITLISTED]: '#856404',
  [ApplicationStatusEnum.CANCELLED]: '#343a40'
}

const resolveUserNameFromObject = (userData) => {
  if (!userData) return ''

  if (typeof userData === 'string') {
    return userData
  }

  if (userData.fullName) {
    return userData.fullName
  }

  if (userData.name) {
    return userData.name
  }

  const firstName = userData.firstName || userData?.user?.firstName || ''
  const lastName = userData.lastName || userData?.user?.lastName || ''
  const combined = `${firstName} ${lastName}`.trim()

  if (combined) {
    return combined
  }

  return userData.email || userData?.user?.email || userData.username || userData.id || ''
}

const ApplicationStatusHistoryModal = ({ isOpen, application, onClose }) => {
  const dispatch = useDispatch()
  const { t, language } = useI18n()
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const statusLabelMap = useMemo(() => {
    const map = {}
    ApplicationStatusOptions.forEach(option => {
      const label = t(option.label)
      const normalizedValue = option.value?.toString().toLowerCase()
      map[option.value] = label
      if (normalizedValue) {
        map[normalizedValue] = label
        map[normalizedValue.toUpperCase()] = label
      }
    })
    return map
  }, [t])

  const formatStatusLabel = (status, { isInitial = false } = {}) => {
    if (!status) {
      if (isInitial) {
        return statusLabelMap[ApplicationStatusEnum.DRAFT] || t('APPLICATIONS_STATUS_HISTORY_STATUS_UNKNOWN')
      }
      return t('APPLICATIONS_STATUS_HISTORY_STATUS_UNKNOWN')
    }
    const normalized = status?.toString()?.toLowerCase()
    return statusLabelMap[normalized] || statusLabelMap[status] || status
  }

  const formatDateTime = (value) => {
    if (!value) return '-'
    try {
      return new Intl.DateTimeFormat(language === 'en' ? 'en-US' : 'tr-TR', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).format(new Date(value))
    } catch {
      return value
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    if (!bytes) return '-'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const index = Math.floor(Math.log(bytes) / Math.log(k))
    return `${Math.round(bytes / Math.pow(k, index) * 100) / 100} ${sizes[index]}`
  }

  const resolveDocumentIcon = (file) => {
    const name = file?.name || ''
    const type = file?.type || ''
    const extension = name.split('.').pop()?.toLowerCase()

    if (type.includes('pdf') || extension === 'pdf') return 'zmdi-collection-pdf'
    if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(extension)) return 'zmdi-image'
    if (type.includes('word') || ['doc', 'docx'].includes(extension)) return 'zmdi-file-text'
    return 'zmdi-attachment'
  }

  const formatChangedBy = (entry) => {
    if (!entry) {
      return t('APPLICATIONS_STATUS_HISTORY_UNKNOWN_USER')
    }

    if (entry.changedUserName) {
      return entry.changedUserName
    }

    if (entry.changedByName) {
      return entry.changedByName
    }

    const resolvedFromObject = resolveUserNameFromObject(entry.changedBy)
    if (resolvedFromObject) {
      return resolvedFromObject
    }

    if (entry.changedByEmail) {
      return entry.changedByEmail
    }

    if (entry.changedById) {
      return `#${entry.changedById}`
    }

    return t('APPLICATIONS_STATUS_HISTORY_UNKNOWN_USER')
  }

  const applicationLabel = useMemo(() => {
    if (!application) {
      return t('APPLICATIONS_STATUS_HISTORY_APPLICATION_FALLBACK')
    }

    const firstName = application?.student?.user?.firstName || ''
    const lastName = application?.student?.user?.lastName || ''
    const fullName = `${firstName} ${lastName}`.trim()
    if (fullName) {
      return fullName
    }

    if (application?.program?.name) {
      return application.program.name
    }

    const applicationId = application?.id?.toString?.()
    if (applicationId) {
      return `#${applicationId.slice(0, 8)}`
    }

    return t('APPLICATIONS_STATUS_HISTORY_APPLICATION_FALLBACK')
  }, [application, t])

  useEffect(() => {
    if (!isOpen || !application?.id) {
      setHistory([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    dispatch(
    ApiCallActions.Get({
        url: `applications/${application.id}/status-history`,
        showLoading: false,
        onSuccess: ({ data }) => {
          const payload = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
          setHistory(payload)
          setLoading(false)
        },
        onError: ({ errorMessage }) => {
          setError(errorMessage || t('APPLICATIONS_STATUS_HISTORY_FETCH_ERROR'))
          setHistory([])
          setLoading(false)
        }
      })
    )
  }, [isOpen, application?.id, dispatch, t])

  const sortedHistory = useMemo(() => {
    return [...history].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
  }, [history])

  if (!application) return null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('APPLICATIONS_STATUS_HISTORY_TITLE')}
      subtitle={t('APPLICATIONS_STATUS_HISTORY_SUBTITLE', { name: applicationLabel })}
      icon="zmdi-time-restore"
      size="large"
    >
      <div className="status-history-modal">
        {loading && (
          <div className="status-history-state">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">{t('APPLICATIONS_STATUS_HISTORY_LOADING')}</span>
            </div>
            <p>{t('APPLICATIONS_STATUS_HISTORY_LOADING')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="status-history-state error">
            <i className="zmdi zmdi-alert-triangle"></i>
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && sortedHistory.length === 0 && (
          <div className="status-history-state empty">
            <i className="zmdi zmdi-time-restore"></i>
            <p>{t('APPLICATIONS_STATUS_HISTORY_EMPTY_TITLE')}</p>
            <span>{t('APPLICATIONS_STATUS_HISTORY_EMPTY_SUBTITLE')}</span>
          </div>
        )}

        {!loading && !error && sortedHistory.length > 0 && (
          <div className="status-history-timeline">
            {sortedHistory.map((entry, index) => {
              const order = sortedHistory.length - index
              const newStatus = entry?.newStatus?.toString()?.toLowerCase()
              const color = statusColorMap[newStatus] || '#4e73df'
              const isInitialEntry = !entry?.oldStatus
              const documentFiles = Array.isArray(entry?.documentFiles) ? entry.documentFiles : []

              return (
                <div className="status-history-item" key={entry.id || `${entry.createdAt}-${index}`}>
                  <div className="status-history-marker">
                    <span className="status-history-order">{order}</span>
                    <span className="status-history-dot" style={{ borderColor: color }}></span>
                    {index !== sortedHistory.length - 1 && <span className="status-history-line"></span>}
                  </div>
                  <div className="status-history-card">
                    <div className="status-history-card-header">
                      <div className="status-history-transition">
                        {isInitialEntry ? (
                          <span className="status-chip" style={{ backgroundColor: color }}>
                            {formatStatusLabel(entry.newStatus)}
                          </span>
                        ) : (
                          <>
                            <span className="status-chip muted">
                              {formatStatusLabel(entry.oldStatus)}
                            </span>
                            <i className="zmdi zmdi-arrow-right status-history-arrow"></i>
                            <span className="status-chip" style={{ backgroundColor: color }}>
                              {formatStatusLabel(entry.newStatus)}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="status-history-date">
                        <i className="zmdi zmdi-time"></i>
                        {formatDateTime(entry.createdAt)}
                      </div>
                    </div>

                    <div className="status-history-meta">
                      <span>
                        <i className="zmdi zmdi-account"></i>
                        {t('APPLICATIONS_STATUS_HISTORY_CHANGED_BY', { user: formatChangedBy(entry) })}
                      </span>
                      {entry.changeReason && (
                        <span>
                          <i className="zmdi zmdi-comment-edit"></i>
                          {t('APPLICATIONS_STATUS_HISTORY_REASON', { reason: entry.changeReason })}
                        </span>
                      )}
                    </div>

                    {documentFiles.length > 0 && (
                      <div className="status-history-documents">
                        <div className="status-history-documents__header">
                          <i className="zmdi zmdi-collection-pdf"></i>
                          <strong>{t('APPLICATIONS_STATUS_HISTORY_DOCUMENTS_TITLE')}</strong>
                        </div>
                        <div className="status-history-documents__list">
                          {documentFiles.map((file, fileIndex) => {
                            const content = (
                              <>
                                <div className="status-history-documents__file">
                                  <i className={`zmdi ${resolveDocumentIcon(file)}`}></i>
                                  <span className="status-history-documents__name">
                                    {file?.name || `Document ${fileIndex + 1}`}
                                  </span>
                                </div>
                                <span className="status-history-documents__meta">{formatFileSize(file?.size)}</span>
                              </>
                            )
                            const key = file?.id || `${file?.name || 'document'}-${fileIndex}`

                            if (file?.url) {
                              return (
                                <a
                                  className="status-history-documents__item"
                                  href={file.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  key={key}
                                >
                                  {content}
                                </a>
                              )
                            }

                            return (
                              <div className="status-history-documents__item is-disabled" key={key}>
                                {content}
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )}

                    {entry.notes && (
                      <div className="status-history-notes">
                        <i className="zmdi zmdi-file-text"></i>
                        <div>
                          <strong>{t('APPLICATIONS_STATUS_HISTORY_NOTES_TITLE')}</strong>
                          <p>{entry.notes}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ApplicationStatusHistoryModal


